import isEqual from 'lodash/isEqual'
import { ETypeOfNotification, InputTextCustom, setNotification,  } from 'medere-ui-framework'
import { Button } from 'primereact/button'
import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'
import { StudiesInformListItemDTO, defaultStudiesInformListItemDTO, StudiesInformDetailsDTO, defaultStudiesInformDetailsDTO, StudyInformDeterminationDTO } from '../../dto/StudiesInformListItemDTO'
import { getStudyById, saveStudyInform, createManualStudyInform, getImagesByStudyOrder, getStudyImageUrl, uploadStudyInformImage, StudyInformImageDTO } from '../../services/StudiesInformController'
import { StudiesInformCard } from './components/StudyInformCard'
import { DeterminationsCard } from './components/DeterminationsCard'
import { ManualStudyInformFields, ManualStudyInformValue, emptyManualStudyInformValue } from './components/ManualStudyInformFields'
import { InputTextarea } from 'primereact/inputtextarea'
import { Chip } from 'primereact/chip'
import { STUDY_STATUS_LABEL, STUDY_STATUS_LABEL_BY_NUMBER } from './constants'

interface StudiesInformCrudPanelProps {
  StudyInformId: number
  setStudyInformId: (StudyInformId: number | null) => void
  mode?: 'edit' | 'view'
  setMode?: (mode: 'edit' | 'view') => void
  onSaved?: () => void
  onShowPdf?: () => void
  onValidate?: () => void
}


function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null
}

function resolveSaveErrorMessage(error: unknown): string {
  if (isRecord(error)) {
    const response = isRecord(error.response) ? error.response : null
    const data = response ? response.data : null
    if (typeof data === 'string' && data.trim() !== '') return data
    if (isRecord(data) && typeof data.message === 'string' && data.message.trim() !== '') return data.message
    if (isRecord(data) && typeof data.error === 'string' && data.error.trim() !== '') return data.error
    if (typeof error.message === 'string' && error.message.trim() !== '') return error.message
  }
  return 'Ocurrió un error inesperado'
}

export default function StudiesInformCrudPanel({
  StudyInformId,
  setStudyInformId,
  mode = 'edit',
  setMode,
  onSaved,
  onShowPdf,
  onValidate,
}: Readonly<StudiesInformCrudPanelProps>) {
  const dispatch = useDispatch()
  const [StudyInform, setStudyInform] = useState<StudiesInformDetailsDTO>({ ...defaultStudiesInformDetailsDTO })
  const [loading, setLoading] = useState(false)
  const [description, setDescription] = useState('')
  const [result, setResult] = useState('')
  const imageInputRef = useRef<HTMLInputElement>(null)
  const [initialStudyInformSnapshot, setInitialStudyInformSnapshot] = useState<StudiesInformDetailsDTO>({ ...defaultStudiesInformDetailsDTO })
  const [pendingImages, setPendingImages] = useState<{ id: string; file: File; previewUrl: string; originalName: string }[]>([])
  const [savedImages, setSavedImages] = useState<StudyInformImageDTO[]>([])
  const [determinations, setDeterminations] = useState<StudyInformDeterminationDTO[]>([])
  const [manualData, setManualData] = useState<ManualStudyInformValue>(emptyManualStudyInformValue)


  const isEditing = StudyInformId > 0
  const isReadOnly = isEditing && mode === 'view'
  // En el informe manual no se puede avanzar hasta elegir paciente y tipo de estudio
  const isManualSelectionPending = !isEditing && (!manualData.patient?.id || !manualData.studyTypeId)
  const isLocked = isReadOnly || isManualSelectionPending
  const studyTypeLabel = isEditing ? StudyInform.studyTypeLabel : manualData.studyTypeLabel
  const isLaboratory = studyTypeLabel?.trim().toLowerCase() === 'laboratorio'

  useEffect(() => {
    if (!isEditing) {
      const initialState = { ...defaultStudiesInformDetailsDTO }
      setStudyInform(initialState)
      setInitialStudyInformSnapshot(initialState)
      setDescription('')
      setResult('')
      setSavedImages([])
      setDeterminations([])
      setManualData(emptyManualStudyInformValue)
      return
    }

    setLoading(true)
    Promise.all([
      getStudyById(StudyInformId),
      getImagesByStudyOrder(StudyInformId),
    ]).then(([resp, images]) => {
      const loadedEntity: StudiesInformDetailsDTO = {
        ...defaultStudiesInformDetailsDTO,
        ...resp,
      }
      setStudyInform(loadedEntity)
      setInitialStudyInformSnapshot(loadedEntity)
      setDescription(loadedEntity.description ?? '')
      setResult(loadedEntity.result ?? '')
      setSavedImages(images)
      setDeterminations(loadedEntity.determinations ?? [])
    }).finally(() => setLoading(false))
  }, [StudyInformId, isEditing])


  function closeCrudPanel() {
      setStudyInformId(null)
  }

  function onAttemptCloseCrudPanel() {
    closeCrudPanel()
  }

  function handleImageUploadChange(event: ChangeEvent<HTMLInputElement>) {
    
    if (isReadOnly) {
      // NO deberia de llegar aca, por botton disabled, pero hacemos doble check.
      console.log('Salio por RO');
      event.target.value = ''
      return
    }

    const selectedFiles = Array.from(event.target.files ?? [])
    event.target.value = ''
    if (selectedFiles.length === 0) return

    const validFiles = selectedFiles.filter((f) => {
      const lower = f.name.toLowerCase()
      return lower.endsWith('.png') || lower.endsWith('.jpg') || lower.endsWith('.jpeg')
    })

    if (validFiles.length < selectedFiles.length) {
      dispatch(setNotification({
        title: 'Archivos inválidos ignorados',
        desc: 'Solo se permiten imágenes JPG o PNG',
        typeOfNotification: ETypeOfNotification.ERROR,
        icon: 'pi pi-times',
      }))
    }
    if (validFiles.length === 0) return

    const newEntries = validFiles.map((file) => ({
      id: `${Date.now()}-${Math.random()}`,
      file,
      previewUrl: URL.createObjectURL(file),
      originalName: file.name,
    }))

    setPendingImages((prev) => [...prev, ...newEntries])
  }

  function removeImage(id: string) {
    setPendingImages((prev) => {
      const target = prev.find((img) => img.id === id)
      if (target) URL.revokeObjectURL(target.previewUrl)
      return prev.filter((img) => img.id !== id)
    })
  }

  async function informar(exitWithInform?: boolean): Promise<boolean> {
    if (isReadOnly) return false
    if (!isEditing) return informarManual(exitWithInform ?? false)

    setLoading(true)

    try {
      await Promise.all([
        saveStudyInform({
          studyOrderId: StudyInform.studyDiagnosticId,
          description: description.trim(),
          result: result.trim(),
          markAsInformed: exitWithInform ?? false,
          determinations: isLaboratory
            ? determinations.map((d) => ({ determinationId: d.determinationId, value: d.value }))
            : [],
        }),
        ...pendingImages.map((img) => uploadStudyInformImage(img.file, StudyInform.studyDiagnosticId)),
      ])

      dispatch(setNotification({
        title: exitWithInform ? 'Informe realizado' : 'Informe guardado',
        desc: 'Los cambios fueron guardados correctamente',
        typeOfNotification: ETypeOfNotification.INFO,
        icon: 'pi pi-check',
      }))

      if (onSaved) onSaved()
      setStudyInformId(null)
      return true
    } catch (error) {
      const message = resolveSaveErrorMessage(error)
      console.error('StudyInform save failed', error)
      dispatch(setNotification({
        title: 'Error',
        desc: message,
        typeOfNotification: ETypeOfNotification.ERROR,
        icon: 'pi pi-times',
      }))
      return false
    } finally {
      setLoading(false)
    }
  }
  
   
  

  async function informarManual(exitWithInform: boolean): Promise<boolean> {
    const { patient, requestingDoctor } = manualData
    const studyTypeId = Number(manualData.studyTypeId)
    // El Combobox de médico puede dejar un objeto con solo el texto tipeado (sin id) si no se eligió una opción de la lista
    const missingFields = [
      !patient?.id && 'paciente',
      !requestingDoctor?.id && 'médico solicitante',
      !studyTypeId && 'tipo de estudio',
    ].filter(Boolean)
    if (!patient?.id || !requestingDoctor?.id || !studyTypeId) {
      dispatch(setNotification({
        title: 'Datos incompletos',
        desc: `Seleccione de la lista: ${missingFields.join(', ')}`,
        typeOfNotification: ETypeOfNotification.ERROR,
        icon: 'pi pi-times',
      }))
      return false
    }

    setLoading(true)

    try {
      const studyOrderId = await createManualStudyInform({
        patientId: patient.id,
        requestingDoctorId: requestingDoctor.id,
        studyTypeId,
        description: description.trim(),
        result: result.trim(),
        markAsInformed: exitWithInform,
        determinations: isLaboratory
          ? determinations.map((d) => ({ determinationId: d.determinationId, value: d.value }))
          : [],
      })
      await Promise.all(pendingImages.map((img) => uploadStudyInformImage(img.file, studyOrderId)))

      dispatch(setNotification({
        title: exitWithInform ? 'Informe realizado' : 'Informe guardado',
        desc: 'Los cambios fueron guardados correctamente',
        typeOfNotification: ETypeOfNotification.INFO,
        icon: 'pi pi-check',
      }))

      if (onSaved) onSaved()
      setStudyInformId(null)
      return true
    } catch (error) {
      const message = resolveSaveErrorMessage(error)
      console.error('Manual StudyInform save failed', error)
      dispatch(setNotification({
        title: 'Error',
        desc: message,
        typeOfNotification: ETypeOfNotification.ERROR,
        icon: 'pi pi-times',
      }))
      return false
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className=' studies-inform-crud-panel'>
      <div className='studies-inform-crud-title-row mb-3'>
        <h2 className='studies-inform-crud-title'>
          <strong>Informar</strong> estudio{!isEditing && ' manual'}
          {isEditing && <Chip
            label={'Estado ' + STUDY_STATUS_LABEL[StudyInform.status]}
            className="border-round ml-4"
            style={{ backgroundColor: '#E0E0E0' }}
            pt={{ label: { className: "text-s font-semibold my-1", style: { color: '#01717C' } } }} />}
        </h2>
        {StudyInform.status === 'SD_INFORMED_PARTIAL' && (
          <Button
            icon='pi pi-pencil'
            className='studies-inform-header-action-btn'
            onClick={() => setMode?.('edit')}
            tooltip='Editar'
            disabled={!isReadOnly}
            tooltipOptions={{ position: 'top' }}
          />
        )}
        {(StudyInform.status === 'SD_INFORMED_PARTIAL' ||
          StudyInform.status === 'SD_INFORMED_COMPLETE' ||
          StudyInform.status === 'SD_VALIDATED') && (
          <Button
            icon='pi pi-file'
            className='studies-inform-header-action-btn'
            onClick={onShowPdf}
            tooltip='Ver PDF'
            tooltipOptions={{ position: 'top' }}
          />
        )}
        {StudyInform.status === 'SD_INFORMED_COMPLETE' && (
          <Button
            icon='pi pi-check-square'
            className='studies-inform-header-action-btn'
            onClick={onValidate}
            tooltip='Validar informe'
            tooltipOptions={{ position: 'top' }}
          />
        )}
      </div>

      {isEditing ? (
        <>
          <StudiesInformCard study={StudyInform} position={0} />
          <StudiesInformCard study={StudyInform} position={1} />
        </>
      ) : (
        <ManualStudyInformFields value={manualData} onChange={setManualData} disabled={loading} />
      )}

      {isManualSelectionPending && (
        <div className='flex align-items-center gap-2 mb-3 text-color-secondary' role='status'>
          <i className='pi pi-info-circle' />
          <span>Seleccione el paciente y el tipo de estudio para continuar con el informe.</span>
        </div>
      )}

      {isLaboratory && (
        <DeterminationsCard
          determinations={determinations}
          onChange={setDeterminations}
          isReadOnly={isLocked}
        />
      )}

      <div className='mt-3 mb-3'>
        <div className='study-inform-card-header'>
          <span className='studies-inform-section-title'>Informe técnico</span>
        </div>

        <div className='studies-inform-crud-field mb-3'>
          <label className='studies-inform-crud-field-label'>Descripción</label>
          <InputTextCustom className='studies-inform-crud-field-input w-full' disable={isLocked} value={description} onChange={(e) => setDescription(e.target.value)} />
        </div>

        <div className='studies-inform-crud-field mb-3'>
          <label className='studies-inform-crud-field-label'>Resultado</label>
          <InputTextarea className='studies-inform-crud-field-input-area' placeholder='Ingrese resultado del estudio'  disabled={isLocked} minLength={8} value={result} onChange={(e) => setResult(e.target.value)} />
        </div>

        <div className='studies-inform-crud-field'>
          <label className='studies-inform-crud-field-label'>Adjuntos</label>
          <input
            ref={imageInputRef}
            type='file'
            accept='.png,.jpg,.jpeg'
            multiple
            className='hidden'
            onChange={handleImageUploadChange}
          />
          <Button
            className='study-inform-upload-btn study-inform-upload-btn-crud'
            onClick={() => imageInputRef.current?.click()}
            disabled={isLocked}
          >
            Cargar <strong>imágenes</strong>
          </Button>

          {(savedImages.length > 0 || pendingImages.length > 0) && (
            <div className='studies-inform-images-grid'>
              {savedImages.map((img) => (
                <div key={`saved-${img.id}`} className='studies-inform-image-item'>
                  <img src={getStudyImageUrl(img.id)} alt={img.description ?? img.fileName} className='studies-inform-image-thumb' />
                  <span className='studies-inform-image-name'>{img.description ?? img.fileName}</span>
                </div>
              ))}
              {pendingImages.map((img) => (
                <div key={img.id} className='studies-inform-image-item'>
                  {!isReadOnly && (
                    <button
                      type='button'
                      className='studies-inform-image-remove-btn'
                      onClick={() => removeImage(img.id)}
                      aria-label='Eliminar imagen'
                    >
                      <i className='pi pi-times' />
                    </button>
                  )}
                  <img src={img.previewUrl} alt={img.originalName} className='studies-inform-image-thumb' />
                  <span className='studies-inform-image-name'>{img.originalName}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className='studies-inform-crud-footer flex justify-content-end gap-2'>
        <Button
          label={isReadOnly ? 'Cerrar' : 'Cancelar'}
          className='studies-inform-crud-secondary-btn'
          outlined
          onClick={onAttemptCloseCrudPanel}
        />
        <Button
          label='Guardar Parcialmente'
          className='studies-inform-crud-secondary-btn'
          outlined
          disabled={isLocked || loading}
          onClick={() => { void informar(false) }}
        />
        <Button
          label='Informar'
          className='studies-inform-crud-primary-btn'
          disabled={isLocked || loading}
          onClick={() => { void informar(true) }}
        />
      </div>
    </div>
  )
}
